import React from 'react';
import { Clock } from 'lucide-react';

/**
 * A time of day on the 24-hour clock — "23:00", never "11:00 pm".
 *
 * The browser's own <input type="time"> follows the computer's locale, so on a
 * 12-hour machine it shows am/pm, and in a narrow box the am/pm is cut off:
 * an 11pm–midnight shift read "11:00 – 12:00". Rotas here are 24-hour
 * everywhere, so this is two plain selects (hour 00–23, minutes in `step`s)
 * that look the same on every computer.
 *
 * @param {string}   value     'HH:MM' (seconds ignored) or ''
 * @param {function} onChange  called with 'HH:MM'
 * @param {number}   step      minute interval offered (default 5); a stored
 *                             value off the interval is still shown
 * @param {'md'|'sm'} size     h-10 (forms) or h-8 (dense rows)
 */
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));

const TimeInput24 = ({
  value, onChange, step = 5, size = 'md', disabled = false, className = '', label = 'Time',
}) => {
  const [h = '', m = ''] = String(value || '').slice(0, 5).split(':');
  const minutes = Array.from({ length: Math.ceil(60 / step) }, (_, i) => String(i * step).padStart(2, '0'));
  if (m && !minutes.includes(m)) minutes.push(m);
  minutes.sort();

  const emit = (hh, mm) => onChange(`${hh || '00'}:${mm || '00'}`);
  const select = 'appearance-none bg-transparent text-center tabular-nums font-medium focus:outline-none cursor-pointer disabled:cursor-not-allowed px-0.5';
  const option = 'bg-white text-[#0B1220] dark:bg-[#1f232a] dark:text-gray-100';

  return (
    <div
      className={`flex items-center gap-1 w-full ${size === 'sm' ? 'h-8' : 'h-10'} border border-[#DCE4EF] dark:border-gray-700 rounded-xl px-2.5 text-[13.5px] text-[#0B1220] dark:text-gray-100 ${disabled ? 'bg-[#F5F7FA] dark:bg-[#20242c] text-[#94A3B8]' : 'bg-white dark:bg-[#1f232a]'} focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-colors ${className}`}
    >
      <Clock className="w-3.5 h-3.5 text-[#94A3B8] shrink-0" aria-hidden="true" />
      <select aria-label={`${label} hour`} className={select} value={h} disabled={disabled} onChange={(e) => emit(e.target.value, m)}>
        {!h && <option className={option} value="">--</option>}
        {HOURS.map((x) => <option key={x} className={option} value={x}>{x}</option>)}
      </select>
      <span className="text-[#94A3B8]">:</span>
      <select aria-label={`${label} minutes`} className={select} value={m} disabled={disabled} onChange={(e) => emit(h, e.target.value)}>
        {!m && <option className={option} value="">--</option>}
        {minutes.map((x) => <option key={x} className={option} value={x}>{x}</option>)}
      </select>
    </div>
  );
};

export default TimeInput24;
